import './Banner.css';
import callApi from 'lib/callApi';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import BannerShell from './BannerShell';

// Banner type
type BannerType = 'info' | 'success' | 'warning' | 'critical';

// API response structure
interface BannerApiResponse {
  enabled: boolean;
  message: string;
  type: BannerType;
  poll_interval?: number;
}

const DEFAULT_POLL_INTERVAL = 300; // Default poll interval in seconds
const CLOSE_ANIMATION_DURATION = 400;
const LOCAL_STORAGE_KEY = 'dismissedBannerMessage';

const Banner: React.FC = () => {
  const [banner, setBanner] = useState<BannerApiResponse | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [dismissedInSession, setDismissedInSession] = useState(false);
  const lastCloseAtRef = useRef<number>(0);

  // Fetch banner from API
  const fetchBanner = useCallback(async () => {
    // While closing, skip making any API calls entirely
    if (isClosing) return;
    // Also skip immediately after close to avoid racing poll call
    const SAFE_WINDOW = CLOSE_ANIMATION_DURATION + 100; // ms
    if (Date.now() - lastCloseAtRef.current < SAFE_WINDOW) return;
    try {
      const res = await callApi('system-settings/banner/', 'GET');
      let data = res?.data as BannerApiResponse;

      if (data) {
        setBanner(data);
        const dismissedMessage = localStorage.getItem(LOCAL_STORAGE_KEY);
        // Only show when not closing to avoid rendering a hidden (faded) banner taking space
        if (
          data.enabled &&
          data.message !== dismissedMessage &&
          !dismissedInSession &&
          !isClosing
        ) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      }
    } catch {
      // Silent fail
    }
  }, [isClosing, dismissedInSession]);

  // Initial fetch
  useEffect(() => {
    fetchBanner();
  }, [fetchBanner]);

  // Always poll for updates regardless of banner state
  useEffect(() => {
    const pollInterval = banner?.poll_interval || DEFAULT_POLL_INTERVAL;
    const interval = setInterval(fetchBanner, pollInterval * 1000);
    return () => clearInterval(interval);
  }, [fetchBanner, banner?.poll_interval]);

  // If message changed from previous one, allow showing again in-session
  useEffect(() => {
    if (banner?.message) {
      setDismissedInSession(false);
    }
  }, [banner?.message]);

  const handleClose = () => {
    lastCloseAtRef.current = Date.now();
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      // In-memory suppression until refresh
      setDismissedInSession(true);
      // Reset closing state after animation completes so future polls can show banner
      setIsClosing(false);
    }, CLOSE_ANIMATION_DURATION);
  };

  const handleDontShow = () => {
    if (banner) {
      localStorage.setItem(LOCAL_STORAGE_KEY, banner.message);
    }
    handleClose();
  };

  // Early exit
  if (!banner?.enabled || !isVisible) return null;

  const iconMap: Record<BannerType, string> = {
    info: 'tim-icons icon-bell-55',
    success: 'tim-icons icon-check-2',
    warning: 'tim-icons icon-alert-circle-exc',
    critical: 'tim-icons icon-alert-circle-exc',
  };

  return (
    <BannerShell
      visible={banner.enabled && isVisible}
      isClosing={isClosing}
      typeClass={banner.type}
      iconClass={iconMap[banner.type]}
      message={banner.message}
      onOk={handleClose}
      extraActions={
        <button
          className="banner-button dont-show-button"
          onClick={handleDontShow}
        >
          Don&apos;t show again
        </button>
      }
    />
  );
};

export default Banner;
